import { useState, useRef, useCallback, useEffect, forwardRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import HTMLFlipBook from "react-pageflip";
import { SEO } from "@/components/SEO";

const TOTAL_PAGES = 30;

const PageImage = forwardRef(({ number }, ref) => (
  <div ref={ref} className="bg-white">
    <img
      src={`/ebook-pages/page-${number}.jpg`}
      alt={`Page ${number}`}
      className="w-full h-full object-contain"
      draggable={false}
    />
  </div>
));

export default function PrarohaEbookPage() {
  const [currentPage, setCurrentPage] = useState(0);
  const flipBook = useRef(null);

  const onFlip = useCallback((e) => setCurrentPage(e.data), []);
  const goNext = () => flipBook.current?.pageFlip()?.flipNext();
  const goPrev = () => flipBook.current?.pageFlip()?.flipPrev();

  // Disable right-click and drag
  useEffect(() => {
    const handler = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);

  return (
    <div
      className="pt-16 md:pt-20 lg:pt-24"
      data-testid="ebook-page"
      onCopy={(e) => e.preventDefault()}
      style={{ userSelect: "none" }}
    >
      <SEO title="Praroha E-Book Guide" description="Read the Praroha Greens e-book guide to microgreens. Learn about growing, nutrition, and recipes." keywords="microgreens ebook, growing guide, nutrition guide" path="/ebook" />
      {/* Hero */}
      <section className="py-8 md:py-10 bg-praroha-forest">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-5 h-5 text-praroha-sage" />
                <span className="text-praroha-sage text-sm font-medium">E-Book</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">
                Praroha Greens E-Book
              </h1>
              <p className="text-white/50 text-sm mt-1">
                Your complete guide to microgreens nutrition and cultivation
              </p>
            </div>
            <span className="text-white/50 text-sm hidden md:block">
              Page {currentPage + 1} of {TOTAL_PAGES}
            </span>
          </motion.div>
        </div>
      </section>

      {/* Flipbook */}
      <section className="py-8 md:py-10 bg-gradient-to-b from-neutral-100 to-neutral-200">
        <div className="flex justify-center mb-6 px-4">
          <HTMLFlipBook
            ref={flipBook}
            width={420}
            height={560}
            size="stretch"
            minWidth={250}
            maxWidth={480}
            minHeight={340}
            maxHeight={640}
            showCover={true}
            mobileScrollSupport={true}
            onFlip={onFlip}
            className="shadow-2xl"
            maxShadowOpacity={0.3}
            drawShadow={true}
            flippingTime={600}
            usePortrait={false}
            autoSize={true}
            useMouseEvents={true}
            swipeDistance={30}
            showPageCorners={true}
          >
            {Array.from({ length: TOTAL_PAGES }, (_, i) => (
              <PageImage key={i} number={i + 1} />
            ))}
          </HTMLFlipBook>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={goPrev}
            disabled={currentPage === 0}
            className="rounded-full"
            data-testid="flip-prev"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <span className="text-sm text-muted-foreground px-4">
            {currentPage + 1} / {TOTAL_PAGES}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={goNext}
            disabled={currentPage >= TOTAL_PAGES - 1}
            className="rounded-full"
            data-testid="flip-next"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>
    </div>
  );
}
